import { seedDialogPageWithOutgoingFormFields } from '@/common/utils/seedDialogPageFromOutgoingForm'
import { buildDialogCloseConfigForStore } from '@/common/utils/dialogCloseParentMergeScope'
import { isStaleDialogOpenAfterClose } from '@/common/utils/dialogPageSlot'
import { resolveParentSlotAtDialogOpen } from '@/common/utils/resolveDialogCloseParentStoreKey'
import { coreService } from '@/services/core/core.service'
import { useAppStore } from '@/stores/app'
import { storeToRefs } from 'pinia'

interface DialogOptions {
  config?: any
  title?: string
  maxWidth?: string
  closable?: boolean
  showFooter?: boolean
}

type OpenDialogResult = {
  success: boolean
  dialogId?: string
  data?: unknown
  error?: string
}

const openDialogInflight = new Map<string, Promise<OpenDialogResult>>()

export function useDialog() {
  const appStore = useAppStore()
  const { currentPlatform } = storeToRefs(appStore)

  const openDialog = async (
    actionId: string,
    context: any,
    options: DialogOptions = {},
  ): Promise<OpenDialogResult> => {
    const normalizedId = String(actionId ?? '').trim()
    if (!normalizedId) {
      return { success: false, error: 'Не указан идентификатор диалога' }
    }

    const existing = openDialogInflight.get(normalizedId)
    if (existing) {
      return existing
    }

    const promise = openDialogOnce(normalizedId, context, options)
    openDialogInflight.set(normalizedId, promise)
    try {
      return await promise
    } finally {
      if (openDialogInflight.get(normalizedId) === promise) {
        openDialogInflight.delete(normalizedId)
      }
    }
  }

  const openDialogOnce = async (
    actionId: string,
    context: any,
    options: DialogOptions = {},
  ): Promise<OpenDialogResult> => {
    try {
      const epochAtStart =
        typeof appStore.getDialogSlotEpoch === 'function'
          ? appStore.getDialogSlotEpoch(actionId)
          : 0

      if (appStore.dialogs[actionId]) {
        await appStore.removeDialog(actionId)
      }

      const parentSlotAtOpen = resolveParentSlotAtDialogOpen(
        appStore,
        context.pageDataSlot ?? context.parentPageDataSlot,
        context.parentPageId,
      )
      const result = await coreService.getDialog({
        params: {
          actionId: Number(actionId),
          sessionId: context.sessionId,
          clientData: {
            fields: {
              fields: context.form || {},
            },
          },
          type: context.platform || currentPlatform.value || undefined,
        },
        config: options.config,
        pageDataSlot: parentSlotAtOpen,
        parentPageDataSlot: parentSlotAtOpen,
      })

      if (
        isStaleDialogOpenAfterClose(
          {
            dialogIds: appStore.dialogIds ?? [],
            dialogSlotEpoch: appStore.dialogSlotEpoch ?? {},
          },
          actionId,
          epochAtStart,
        )
      ) {
        return { success: false, error: 'Диалог был закрыт до завершения открытия' }
      }

      if (result.form) {
        const platform = context.platform || currentPlatform.value || undefined

        if (platform) {
          result.uniData = result.uniData || {}
          result.uniData.clientData = {
            ...(result.uniData.clientData || {}),
            platform,
          }
        }

        const mergedCloseCfg = buildDialogCloseConfigForStore(
          result.clientDialogCloseConfig && typeof result.clientDialogCloseConfig === 'object'
            ? (result.clientDialogCloseConfig as Record<string, unknown>)
            : {},
          {
            ...(options.config && typeof options.config === 'object' ? options.config : {}),
          },
        )
        if (parentSlotAtOpen) {
          mergedCloseCfg.pageDataSlot = parentSlotAtOpen
          mergedCloseCfg.onCloseDialogParentSlotId = parentSlotAtOpen
        }
        appStore.addDialog({
          id: actionId,
          sessionId: result.sessionId,
          config: Object.keys(mergedCloseCfg).length ? mergedCloseCfg : options.config,
          data: result,
        })

        const { ensureWebClientOnLoadBeforeOpenForm } = await import(
          '@/services/action/webClientFormOnLoadBeforeBuild'
        )
        const prepared = await ensureWebClientOnLoadBeforeOpenForm(result as Record<string, unknown>, {
          appStore,
          openedFormId: actionId,
          parseActionId: actionId,
        })
        appStore.setPageData(actionId, prepared)
        seedDialogPageWithOutgoingFormFields(
          appStore,
          String(actionId),
          (context.form && typeof context.form === 'object' ? context.form : {}) as Record<
            string,
            unknown
          >,
        )

        return {
          success: true,
          dialogId: actionId,
          data: result,
        }
      }

      return {
        success: false,
        error: 'Не удалось открыть диалог',
      }
    } catch (error) {
      console.error('Error opening dialog:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Ошибка при открытии диалога',
      }
    }
  }

  const closeDialog = async (dialogId: string) => {
    try {
      const dialog = appStore.dialogs[dialogId]
      if (dialog?.sessionId) {
        await coreService.removeDialog(dialogId, dialog.sessionId)
      }

      appStore.removeDialog(dialogId)
      return { success: true }
    } catch (error) {
      console.error('Error closing dialog:', error)
      appStore.removeDialog(dialogId)
      return { success: true }
    }
  }

  const closeAllDialogs = async () => {
    const dialogIds = [...appStore.dialogIds]

    for (const dialogId of dialogIds) {
      await closeDialog(dialogId)
    }
  }

  const getDialog = (dialogId: string) => {
    return appStore.dialogs[dialogId]
  }

  const hasActiveDialogs = () => {
    return appStore.dialogIds.length > 0
  }

  return {
    openDialog,
    closeDialog,
    closeAllDialogs,
    getDialog,
    hasActiveDialogs,
    activeDialogs: appStore.dialogIds.map((id) => appStore.dialogs[id]),
  }
}
